import { useState, useRef, useCallback } from "react";

interface IDCardUploadProps {
  frontImage: string | null;
  backImage: string | null;
  onFrontChange: (val: string | null) => void;
  onBackChange: (val: string | null) => void;
  disabled?: boolean;
}

const MAX_DIM = 1200;
const QUALITY_STEPS = [0.72, 0.6, 0.45];
/* data URL 長度上限（約 450KB 二進位），避免手機網路把兩張大圖傳不出去 */
const MAX_DATA_URL_LEN = 600000;
const MAX_FILE_MB = 10;

/* 直接把檔案讀成 data URL（任何檔案都不會失敗，作為安全後備） */
function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("檔案讀取失敗"));
    reader.readAsDataURL(file);
  });
}

/* 用 object URL 載入圖片（比 base64 更省記憶體，也更能吃 HEIC 等格式） */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("圖片無法解碼"));
    };
    img.src = url;
  });
}

/* 優先壓縮；任何一步失敗就退回原始檔案，確保上傳永遠能成功 */
async function processImage(file: File): Promise<string> {
  try {
    const img = await loadImage(file);
    let w = img.naturalWidth || img.width;
    let h = img.naturalHeight || img.height;
    if (!w || !h) throw new Error("無效的圖片尺寸");

    if (w > MAX_DIM || h > MAX_DIM) {
      const scale = MAX_DIM / Math.max(w, h);
      w = Math.round(w * scale);
      h = Math.round(h * scale);
    }

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas not supported");

    // 白底，避免透明 PNG 轉 JPEG 後變黑
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);

    const dataUrl = (() => {
      let best = "";
      for (const q of QUALITY_STEPS) {
        best = canvas.toDataURL("image/jpeg", q);
        if (best && best.length > 100 && best.length <= MAX_DATA_URL_LEN) break;
      }
      return best;
    })();
    if (!dataUrl || dataUrl.length < 100 || dataUrl === "data:,") {
      throw new Error("壓縮結果無效");
    }
    return dataUrl;
  } catch {
    // 後備：直接使用原始檔案資料（超過大小上限則拒絕，避免上傳卡死）
    const raw = await fileToDataUrl(file);
    if (!raw || raw.length < 100) throw new Error("圖片處理失敗");
    if (raw.length > MAX_DATA_URL_LEN * 1.6) {
      throw new Error("圖片檔案過大，請改用相機重新拍一張再上傳");
    }
    return raw;
  }
}

function UploadBox({
  label,
  sublabel,
  icon,
  image,
  onImageChange,
  disabled,
}: {
  label: string;
  sublabel: string;
  icon: string;
  image: string | null;
  onImageChange: (val: string | null) => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState("");

  const processFile = useCallback(
    async (file: File) => {
      setError("");
      // 有些手機（HEIC 等）回報的 type 可能是空字串，放行讓後續解碼判斷
      if (file.type && !file.type.startsWith("image/")) {
        setError("僅支援圖片檔案（JPG / PNG / HEIC）");
        return;
      }
      if (file.size > MAX_FILE_MB * 1024 * 1024) {
        setError(`檔案過大，請選擇 ${MAX_FILE_MB}MB 以下的圖片`);
        return;
      }
      try {
        const processed = await processImage(file);
        onImageChange(processed);
      } catch (err) {
        console.error("[IDCardUpload] processImage error:", err);
        const msg = err instanceof Error ? err.message : "";
        setError(msg || "圖片處理失敗，請重新上傳或改用其他照片");
      }
    },
    [onImageChange]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <p className="font-body text-xs font-medium text-stone-500">
        {label}
        <span className="text-stone-300 font-normal ml-1">{sublabel}</span>
      </p>

      <div
        onClick={() => !disabled && !image && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled && !image) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={[
          "relative rounded-xl border-2 border-dashed overflow-hidden cursor-pointer transition-all",
          "flex items-center justify-center",
          "h-36 md:h-40",
          image ? "border-stone-200" : dragOver ? "border-rose-400 bg-rose-50/40" : "border-stone-300 bg-stone-50 hover:border-stone-400 hover:bg-stone-100/60",
          disabled && !image ? "opacity-50 cursor-not-allowed" : "",
        ].join(" ")}
      >
        {image ? (
          <>
            <img
              src={image}
              alt={label}
              className="w-full h-full object-contain"
            />
            {!disabled && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center gap-2 opacity-0 hover:opacity-100 transition-opacity">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    inputRef.current?.click();
                  }}
                  className="font-body text-xs font-medium px-3 py-1.5 rounded-full bg-white text-stone-800 hover:bg-stone-100 transition-colors whitespace-nowrap"
                >
                  <i className="ri-refresh-line mr-1" />
                  更換
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onImageChange(null);
                  }}
                  className="font-body text-xs font-medium px-3 py-1.5 rounded-full bg-red-50 text-red-700 hover:bg-red-100 transition-colors whitespace-nowrap"
                >
                  <i className="ri-delete-bin-line mr-1" />
                  刪除
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center gap-2 text-center px-4">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-stone-200/70">
              <i className={`${icon} text-stone-500 text-lg`} />
            </div>
            <p className="font-body text-[11px] text-stone-400 leading-relaxed">
              點擊或拖曳上傳
              <br />
              <span className="text-stone-300">JPG、PNG，最大 {MAX_FILE_MB}MB</span>
            </p>
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/*,.heic,.heif"
          onChange={handleInputChange}
          className="hidden"
          disabled={disabled}
        />
      </div>

      {error && (
        <p className="font-body text-[11px] text-red-500 flex items-center gap-1">
          <i className="ri-error-warning-line" />
          {error}
        </p>
      )}
    </div>
  );
}

export default function IDCardUpload({
  frontImage,
  backImage,
  onFrontChange,
  onBackChange,
  disabled,
}: IDCardUploadProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <UploadBox
        label="身分證正面"
        sublabel="（人像面）"
        icon="ri-id-card-line"
        image={frontImage}
        onImageChange={onFrontChange}
        disabled={disabled}
      />
      <UploadBox
        label="身分證反面"
        sublabel="（國徽面）"
        icon="ri-landscape-line"
        image={backImage}
        onImageChange={onBackChange}
        disabled={disabled}
      />
    </div>
  );
}